import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  getClubDetails,
  type Product,
  type RecurrenceType,
  type ClubDetails,
} from "../../services/api";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { AdminCard } from "../../components/admin/AdminCard";
import { useAgeGroupOptions } from "../../hooks/useAgeGroupOptions";

export default function Products() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClubId } = useAuth();

  const recurrenceLabels: Record<RecurrenceType, string> = {
    hourly: t("products.recurrence_hourly", "Pe oră"),
    weekly: t("products.recurrence_weekly", "Săptămânal"),
    monthly: t("products.recurrence_monthly", "Lunar"),
  };

  const [products, setProducts] = useState<Product[]>([]);
  const [clubDetails, setClubDetails] = useState<ClubDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: "",
    price: "",
    recurrence_type: "monthly" as RecurrenceType,
    age_groups: [] as string[],
    description: "",
  });

  // Every age group the platform defines, plus anything this club or the
  // product being edited already uses. Previously this fell back to a
  // hardcoded U-8…Seniori list, so groups the platform actually had were
  // missing from the picker.
  const ageGroups = useAgeGroupOptions(clubDetails?.age_groups, form.age_groups);

  useEffect(() => {
    if (activeClubId) {
      fetchData();
    }
  }, [activeClubId]);

  const fetchData = async () => {
    if (!activeClubId) return;
    try {
      setLoading(true);
      const [productsData, clubData] = await Promise.all([
        getProducts(activeClubId),
        getClubDetails(activeClubId),
      ]);
      setProducts(productsData);
      setClubDetails(clubData);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error(t("products.error_load", "Nu s-au putut încărca datele"));
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setForm({
      name: "",
      price: "",
      recurrence_type: "monthly",
      age_groups: [],
      description: "",
    });
    setShowModal(true);
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      price: (product.price_cents / 100).toString(),
      recurrence_type: product.recurrence_type,
      age_groups: product.age_groups,
      description: product.description || "",
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeClubId) return;

    if (!form.name.trim()) {
      toast.error(t("products.name_required", "Numele este obligatoriu"));
      return;
    }

    const priceCents = Math.round(parseFloat(form.price || "0") * 100);
    if (priceCents <= 0) {
      toast.error(t("products.price_required", "Prețul trebuie să fie mai mare decât 0"));
      return;
    }

    setSaving(true);
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, {
          name: form.name.trim(),
          price_cents: priceCents,
          recurrence_type: form.recurrence_type,
          age_groups: form.age_groups,
          description: form.description.trim() || undefined,
        });
        toast.success(t("products.updated", "Produs actualizat!"));
      } else {
        await createProduct({
          club_id: activeClubId,
          name: form.name.trim(),
          price_cents: priceCents,
          recurrence_type: form.recurrence_type,
          age_groups: form.age_groups,
          description: form.description.trim() || undefined,
        });
        toast.success(t("products.created", "Produs creat!"));
      }
      setShowModal(false);
      fetchData();
    } catch (error: any) {
      console.error("Failed to save product:", error);
      toast.error(error.response?.data?.error || t("products.error_save", "Nu s-a putut salva produsul"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId: string) => {
    if (deleteConfirmId !== productId) {
      setDeleteConfirmId(productId);
      setTimeout(() => setDeleteConfirmId(null), 3000);
      return;
    }

    try {
      await deleteProduct(productId);
      toast.success(t("products.deleted", "Produs șters!"));
      setDeleteConfirmId(null);
      fetchData();
    } catch (error: any) {
      console.error("Failed to delete product:", error);
      toast.error(error.response?.data?.error || t("products.error_delete", "Nu s-a putut șterge produsul"));
    }
  };

  const toggleAgeGroup = (group: string) => {
    setForm((prev) => ({
      ...prev,
      age_groups: prev.age_groups.includes(group)
        ? prev.age_groups.filter((g) => g !== group)
        : [...prev.age_groups, group],
    }));
  };

  const formatPrice = (cents: number, currency: string = "RON") => {
    return new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  };

  if (!activeClubId) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-slate-600">{t("products.no_active_club", "Nu există club activ selectat")}</p>
        </div>
      </ClubAdminLayout>
    );
  }

  if (loading) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
        </div>
      </ClubAdminLayout>
    );
  }

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <AdminPageHeader
          title={t("products.title", "Produse & Servicii")}
          subtitle={`${t("products.subtitle", "Gestionează produsele și serviciile oferite de club")} (${products.length})`}
          icon={<ClubNavIcon id="products" className="w-6 h-6" />}
          actions={
            <AdminButton onClick={openCreateModal} icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}>
              {t("products.add_product", "Adaugă Produs")}
            </AdminButton>
          }
        />

        {/* Products Grid */}
        {products.length === 0 ? (
          <AdminCard className="p-12 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-slate-700">{t("products.no_products", "Niciun produs")}</h3>
            <p className="mb-6 text-slate-500">{t("products.no_products_desc", "Începe prin a adăuga primul produs sau serviciu.")}</p>
            <button
              onClick={openCreateModal}
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-all"
            >
              {t("products.add_first", "Adaugă Primul Produs")}
            </button>
          </AdminCard>
        ) : (
          <div className="space-y-4">
            {/* Count / summary line */}
            <div className="flex items-center gap-2 px-1 text-[13px] font-semibold text-slate-500">
              <span className="w-2 h-2 rounded-[3px] bg-indigo-500" />
              <span>
                {products.length}{" "}
                {products.length === 1
                  ? t("products.count_one", "produs")
                  : t("products.count_many", "produse")}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {products.map((product) => (
                <AdminCard
                  key={product.id}
                  className={`p-5 flex flex-col transition-all hover:shadow-md ${
                    !product.is_active ? "opacity-60" : ""
                  }`}
                >
                  {/* Header: name + actions */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-[15px] font-bold text-slate-800 leading-tight truncate">{product.name}</h3>
                      {!product.is_active && (
                        <span className="inline-block px-2 py-0.5 mt-1.5 bg-rose-50 text-rose-700 text-xs font-semibold rounded-md">
                          {t("common.inactive", "Inactiv")}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEditModal(product)}
                        title={t("common.edit", "Editează")}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        title={t("common.delete", "Șterge")}
                        className={`p-2 rounded-lg transition-colors ${
                          deleteConfirmId === product.id
                            ? "text-white bg-rose-600 hover:bg-rose-700"
                            : "text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Price + recurrence */}
                  <div className="flex items-baseline gap-2 mt-4">
                    <span className="text-2xl font-extrabold text-indigo-600 tabular-nums">
                      {formatPrice(product.price_cents, product.currency)}
                    </span>
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-md">
                      {recurrenceLabels[product.recurrence_type]}
                    </span>
                  </div>

                  {product.description && (
                    <p className="mt-3 text-sm text-slate-500 line-clamp-3">{product.description}</p>
                  )}

                  {product.age_groups.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-slate-100">
                      {product.age_groups.map((group) => (
                        <span
                          key={group}
                          className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-md"
                        >
                          {group}
                        </span>
                      ))}
                    </div>
                  )}
                </AdminCard>
              ))}
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-800">
                  {editingProduct ? t("products.edit_product", "Editează Produs") : t("products.add_new", "Adaugă Produs Nou")}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <div className="p-5 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("products.product_name", "Nume Produs *")}
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t("products.name_placeholder", "ex: Abonament Lunar")}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("products.price_label", "Preț (RON) *")}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      placeholder="150.00"
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("products.recurrence_label", "Recurență *")}
                    </label>
                    <select
                      value={form.recurrence_type}
                      onChange={(e) => setForm({ ...form, recurrence_type: e.target.value as RecurrenceType })}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="hourly">{t("products.recurrence_hourly", "Pe oră")}</option>
                      <option value="weekly">{t("products.recurrence_weekly", "Săptămânal")}</option>
                      <option value="monthly">{t("products.recurrence_monthly", "Lunar")}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("products.age_groups", "Categorii de vârstă")}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {ageGroups.map((group: string) => (
                      <button
                        key={group}
                        type="button"
                        onClick={() => toggleAgeGroup(group)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          form.age_groups.includes(group)
                            ? "bg-indigo-600 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {group}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {t("products.age_groups_hint", "Lasă necompletat pentru toate categoriile")}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("common.description", "Descriere")}
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder={t("products.description_placeholder", "Descriere opțională...")}
                    rows={3}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                  />
                </div>

                </div>

                <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-sm"
                  >
                    {t("common.cancel", "Anulează")}
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-sm disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {t("common.saving", "Se salvează...")}
                      </>
                    ) : editingProduct ? (
                      t("common.save", "Salvează")
                    ) : (
                      t("common.add", "Adaugă")
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </ClubAdminLayout>
  );
}
